import { ErrorBoundary } from '@/components/Rollbar';
import { CardTableSkeleton } from '@/components/CardTable';
import { Wrapper } from '@/components/OpenFeature/Wrapper';
import { JournalInfoCards } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Cards/JournalInfoCards';
import { ShowRemainingButton } from '@/components/InvoiceReportDetailDrawer/parts/ShowRemainingButton';
import { ControlArea } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/ControlArea';
import {
  JournalLayout,
  JournalLayouts,
  useJournalLayout,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/hooks/useJournalLayout';
import { useJournalRemaining } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/hooks/useJournalRemaining';
import {
  useDefaultPagination,
  useJournalEntries,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/hooks/useJournalEntries';
import { filterColumns } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/Columns/helpers';
import {
  useColumns,
  type JournalEntryRow,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/Columns';
import {
  ErrorJournalInfoTable,
  LoadingJournalInfoTable,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/JournalInfo/Table/Table';
import {
  type InternalTableProps,
  VirtualTable,
} from '@/components/InvoiceReportDetailDrawer/parts/VirtualTable';
import { useTranslation } from '@/i18n';
import { getScrollableAncestor } from '@/utils/dom/getScrollableAncestor';
import {
  FC,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

// `useLayoutEffect` restores scroll before paint (no visible jump) but warns
// under SSR; fall back to `useEffect` on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Only offer "show remaining" once there are more rows than the initial
// reveal. The same threshold and expand/collapse state apply to both
// layouts via the single `useJournalRemaining` call below.
const SHOW_REMAINING_THRESHOLD = 10;

type JournalInfoLayoutSwitchProps = { reportId: string };

type InnerListProps = JournalInfoLayoutSwitchProps & {
  layout: JournalLayout;
  setLayout: (layout: JournalLayout) => void;
};

const InnerList: FC<InnerListProps> = memo(
  ({ reportId, layout, setLayout }) => {
    const { t } = useTranslation();
    const { data: allData, listColumns } = useJournalEntries(reportId);
    // Shared between both layouts: expand/collapse state (and therefore the
    // revealed record count) persists across a card<->table layout switch,
    // since both branches read from this single hook call.
    const {
      data: slicedData,
      isExpanded,
      showRemaining,
      collapse,
    } = useJournalRemaining(allData, reportId);
    const pagination = useDefaultPagination();

    // Card and table layouts have very different heights, so swapping them
    // shifts the drawer's scrollable content and the browser clamps the
    // scroll — the pane appears to jump to the top. Capture the scroll
    // container's position on switch and restore it before paint so toggling
    // layouts stays put. Mirrors `TransactionsDetailLayoutSwitch`.
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const scrollElRef = useRef<Element | null>(null);
    const savedScrollTopRef = useRef<number | null>(null);

    const handleLayoutChange = useCallback(
      (next: JournalLayout) => {
        const scrollEl = getScrollableAncestor(sentinelRef.current);
        scrollElRef.current = scrollEl;
        savedScrollTopRef.current = scrollEl ? scrollEl.scrollTop : null;
        setLayout(next);
      },
      [setLayout]
    );

    useIsomorphicLayoutEffect(() => {
      const scrollEl = scrollElRef.current;
      const saved = savedScrollTopRef.current;
      if (scrollEl && saved !== null) {
        scrollEl.scrollTop = saved;
        savedScrollTopRef.current = null;
      }
    }, [layout]);

    // Approval drawer (this layout switch): clamp-only tooltip (NA-5596 AC4).
    const columns = useColumns(false);
    const filteredColumns = useMemo(() => {
      if (!listColumns) return columns;
      return filterColumns(columns, listColumns);
    }, [columns, listColumns]);

    const table: InternalTableProps<JournalEntryRow> = useMemo(
      () => ({
        data: slicedData,
        columns: filteredColumns,
        emptyText: <>{t('journal_info_empty_message')}</>,
      }),
      [slicedData, filteredColumns, t]
    );

    const showRemainingButton =
      allData.length > SHOW_REMAINING_THRESHOLD ? (
        <ShowRemainingButton
          isExpanded={isExpanded}
          onShowRemaining={showRemaining}
          onCollapse={collapse}
          collapseLabel={t('journal_entry.collapse')}
          showAllLabel={t('journal_entry.show_all')}
          testIdPrefix='journal-info'
        />
      ) : undefined;

    return (
      <>
        {/* Zero-size anchor used only to locate the scroll container. */}
        <span
          ref={sentinelRef}
          aria-hidden='true'
          style={{ display: 'none' }}
        />
        <ControlArea
          count={allData.length}
          layout={layout}
          onLayoutChange={handleLayoutChange}
        />
        {layout === JournalLayouts.card ? (
          <JournalInfoCards
            columns={filteredColumns}
            data={slicedData}
            totalRecords={allData.length}
            footer={showRemainingButton}
          />
        ) : (
          <VirtualTable
            table={table}
            pagination={pagination}
            testId='journal-info-table'
            footer={showRemainingButton}
          />
        )}
      </>
    );
  }
);
InnerList.displayName = 'JournalInfoLayoutSwitchInnerList';

export const JournalInfoLayoutSwitch: FC<JournalInfoLayoutSwitchProps> = memo(
  ({ reportId }) => {
    // Owned here so the fallback can match the layout, and kept to a SINGLE
    // instance passed into `InnerList` — see `TransactionsDetailLayoutSwitch`.
    const { layout, setLayout } = useJournalLayout();

    return (
      <Wrapper isProto={false}>
        <ErrorBoundary fallback={ErrorJournalInfoTable}>
          <Suspense
            fallback={
              layout === JournalLayouts.card ? (
                <CardTableSkeleton testId='journal-info-cards-skeleton' />
              ) : (
                <LoadingJournalInfoTable />
              )
            }
          >
            <InnerList
              reportId={reportId}
              layout={layout}
              setLayout={setLayout}
            />
          </Suspense>
        </ErrorBoundary>
      </Wrapper>
    );
  }
);
JournalInfoLayoutSwitch.displayName = 'JournalInfoLayoutSwitch';
